import { IMAGES_METRICS } from "@booking/consts";
import { ImageProviderEnum, ImageVariantEnum } from "@booking/enums";
import type { ImageDataType } from "@booking/types";
import { DatePlannerDatumType } from "@quote/types";
import { format, formatDate, formatISO, isValid, parse } from "date-fns";
import React, { type CSSProperties } from "react";

import { gaDialogShown } from "@/booking/utils/tracking";
import { SourceProps } from "@/components/atoms/media/Source";
import { DATE_FORMATS, IMAGE_TYPES, LOYALTY_CODE_PATTERN } from "@/consts";
import { ImageMetric, type CSSVariable } from "@/types";

function addInvalidDate(date: Date) {
  return {
    date,
    disabled: true,
    duration: 0,
  } as DatePlannerDatumType;
}

function arrayToCSSVariables(values: Array<number | string>, prefix: string) {
  const obj: Record<string, number | string> = {};

  values.forEach((value, index) => {
    obj[`${prefix}${index}`] = value;
  });

  return setCSSVariables(obj);
}

function containerCSSVars(
  windowSize: Record<string, number | null>,
  image?: ImageDataType,
) {
  const values: Record<string, number | string> = {};

  if (image && image.src && image.provider !== ImageProviderEnum.BUCKET) {
    const width = containerWidth(windowSize, image.variant);

    if (width) {
      values["img-w"] = width;
    }
  }

  return values;
}

function containerWidth(
  windowSize: any,
  variant: ImageVariantEnum = ImageVariantEnum.NONE,
) {
  const metrics = Object.entries(IMAGES_METRICS[variant])
    .reverse()
    .find(([width]) => parseInt(width, 10) <= windowSize.width + 1);

  if (metrics) {
    return `${metrics[1].width}px`;
  }

  return "";
}

function createId(componentType: string = "") {
  return [componentType.toLowerCase(), randomStr()].filter(Boolean).join("_");
}

function debounce(callback: Function, delay: number, immediate?: boolean) {
  let timeout: any | null;

  return function debounce(this: any, ...args: any[]) {
    const context = this;

    function apply() {
      timeout = null;
      if (!immediate) {
        callback.apply(context, args);
      }
    }

    if (immediate && !timeout) {
      callback.apply(context, args);
    }

    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(apply, delay);
  };
}

function errorMessagesProxy(
  errors: Record<Uppercase<string>, string> & {
    GENERIC_ERROR: string;
  },
  errorFunctions?: Record<Uppercase<string>, (...args: any[]) => string>,
) {
  return new Proxy(
    { ...errors, ...errorFunctions },
    {
      get(target, prop: keyof typeof errors | keyof typeof errorFunctions) {
        const err = target[prop];

        return err ?? errors.GENERIC_ERROR;
      },
      set() {
        throw new Error("Cannot set errors on this object.");
      },
    },
  );
}

function formatCurrency(
  value: number,
  locales: string | Array<string> = "en-EN",
  currency = "GBP",
) {
  const maximumFractionDigits = value % 1 === 0 ? 0 : undefined;
  const minimumFractionDigits = maximumFractionDigits;

  return Intl.NumberFormat(locales, {
    currency,
    maximumFractionDigits,
    minimumFractionDigits,
    style: "currency",
  }).format(value);
}

function randomStr() {
  return Math.floor(Math.random() * 1e6).toString(36);
}

function setCSSVariables(values: Record<string, number | string>) {
  return Object.entries(values).reduce((values, [key, value]) => {
    const variable = { [`--${key}`]: value } as CSSVariable;

    values = {
      ...values,
      ...variable,
    };

    return values;
  }, {} as CSSProperties);
}

function toSnakeCase(string: string) {
  return string
    .split(/(?=[A-Z])/)
    .join("_")
    .toLowerCase();
}

function formatNumberWithCommas(value: number) {
  return value.toLocaleString("en-EN");
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function formatToTime(dateString: string) {
  const date = parse(dateString, "yyyy-MM-dd HH:mm", new Date());

  if (isValid(date)) {
    return format(date, DATE_FORMATS.HOURS_MINUTES);
  }

  return dateString;
}

function renderChildren(id: string, children: React.ReactNode) {
  const items = React.Children.toArray(children);

  if (items.length === 0) {
    return null;
  }

  return items.map((child, index) => (
    <React.Fragment key={`${id}_${index}`}>{child}</React.Fragment>
  ));
}

function datasetFlag(condition: boolean) {
  return condition ? "" : null;
}

function mapEntries(
  obj: Record<any, any>,
  fn: (arr: [any, any]) => [any, any],
): Record<any, any> {
  return Object.fromEntries(Object.entries(obj).map(fn));
}

function underscore(str: string): string {
  return str
    .split(/(?=[A-Z])/)
    .join("_")
    .toLowerCase();
}

function underscoreKeys(obj: object): object {
  return mapEntries(obj, ([k, v]) => {
    let value = v;

    if (Array.isArray(v)) {
      value = v.map(underscoreKeys);
    } else if (typeof v === "object") {
      value = underscoreKeys(v);
    }

    return [underscore(k), value];
  });
}

function openDialog(id: string, eventGroup?: string) {
  const dialog = document.getElementById(id) as HTMLDialogElement;

  if (dialog) {
    dialog.showModal();
    gaDialogShown({ eventGroup, id });
  }
}

function closeDialog(e: any) {
  const { target } = e;
  const button: HTMLButtonElement = target.closest("button");
  const dialog: HTMLDialogElement = target.closest("dialog");

  if ((button && !button.dataset.action) || target === dialog) {
    // click on close button or backdrop
    dialog.close();
  }
}

function findFloorValue(n: number, arr: number[]) {
  return arr.reduce((floor, v) => {
    if (v <= n && v > floor) return v;
    return floor;
  }, arr[0]);
}

function camelize(str: string): string {
  return str.replace(/[-_](.)/g, (_, char) => char.toUpperCase());
}

const ordinalShortMonthDay = (date: Date) =>
  formatDate(date, DATE_FORMATS.ORDINAL_SHORT_MONTH_DAY);

function cloudinaryParams(metrics: ImageMetric) {
  const { height, width } = metrics;
  let { ar } = metrics;

  if (!ar && height && height === width) {
    ar = "1:1";
  }

  return [
    "q_80",
    ar ? `c_auto,ar_${ar}` : "c_fill",
    height ? `h_${height * 2}` : undefined,
    width ? `w_${width * 2}` : undefined,
  ].filter(Boolean);
}

function cloudinarySource(
  src: string,
  mediaWidth: number,
  metrics: ImageMetric,
): Array<SourceProps> {
  const params = cloudinaryParams(metrics).join(",");
  const srcset = src
    .replace("upload/", `upload/${params}/`)
    .replace(/\.webp$/, ".jpg"); // in case a WebP reference was sent

  return IMAGE_TYPES.map((imageType) => {
    const props: SourceProps = { srcset };

    if (mediaWidth > 0) {
      props.media = `(min-width: ${mediaWidth}px)`;
    }

    if (imageType !== "jpg") {
      props.srcset = srcset.replace(/\.jpg$/, ".webp");
      props.type = `image/${imageType}`;
    }

    return props;
  });
}

function contentfulParams(metrics: ImageMetric) {
  const { height, width } = metrics;

  return [
    height ? `h=${height}` : undefined,
    width ? `w=${width}` : undefined,
    height || width ? "fit=scale" : undefined,
  ].filter(Boolean);
}

function contentfulSource(
  src: string,
  mediaWidth: number,
  metrics: ImageMetric,
): Array<SourceProps> {
  const params = contentfulParams(metrics).join("&");

  return IMAGE_TYPES.map((imageType) => {
    return {
      media: mediaWidth > 0 ? `(min-width: ${mediaWidth}px)` : null,
      srcset: `${src}?fm=${imageType}&${params}`,
      type: imageType === "jpg" ? null : `image/${imageType}`,
    } as SourceProps;
  });
}

const getPaths = (paths: any): string[] => {
  if (typeof paths === "string") {
    return [paths];
  }
  if (typeof paths === "object" && paths !== null) {
    return Object.values(paths).flatMap(getPaths);
  }
  return [];
};

function getEnumKeyByEnumValue<
  TEnumKey extends string,
  TEnumVal extends string | number,
>(enumObject: { [key in TEnumKey]: TEnumVal }, enumValue: TEnumVal) {
  const keys = (Object.keys(enumObject) as Array<TEnumKey>).filter(
    (x) => enumObject[x] === enumValue,
  );

  return keys.length > 0 ? keys[0] : undefined;
}

function isoDate(date: Date): string {
  return formatISO(date, { representation: "date" });
}

const omitKey = <T extends Record<string | number, any>, K extends keyof T>(
  obj: T,
  keys: K | K[],
): Omit<T, K> => {
  const keysArray = Array.isArray(keys) ? keys : [keys];
  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => !keysArray.includes(key as K)),
  ) as Omit<T, K>;
};

const isLoyaltyCode = (code: string): boolean => {
  const normalizedCode = code?.trim() ?? "";
  return LOYALTY_CODE_PATTERN.test(normalizedCode);
};

const isEmpty = (obj: any) =>
  [Object, Array].includes((obj || {}).constructor) &&
  !Object.entries(obj || {}).length;

const getSearchParam = (key: string): string | null => {
  return new URLSearchParams(window.location.search).get(key);
};

const createNumberOptions = (start: number, end: number) => {
  const length = end - start + 1;

  return Array.from({ length }, (_, i) => ({
    text: (i + start).toString(),
    value: (i + start).toString(),
  }));
};

export {
  addInvalidDate,
  arrayToCSSVariables,
  camelize,
  closeDialog,
  cloudinaryParams,
  cloudinarySource,
  containerCSSVars,
  containerWidth,
  contentfulParams,
  contentfulSource,
  createId,
  datasetFlag,
  debounce,
  errorMessagesProxy,
  findFloorValue,
  formatCurrency,
  formatNumberWithCommas,
  formatToTime,
  getEnumKeyByEnumValue,
  getPaths,
  isLoyaltyCode,
  isoDate,
  mapEntries,
  omitKey,
  openDialog,
  ordinalShortMonthDay,
  randomStr,
  renderChildren,
  setCSSVariables,
  toSnakeCase,
  underscore,
  underscoreKeys,
  wait,
  isEmpty,
  getSearchParam,
  createNumberOptions,
};
